/**
 * RangeSlider Component
 * 
 * A customizable range input with label, value display, and optional scale markers.
 * 
 * @param {string} label - Label text for the slider
 * @param {number} value - Current value
 * @param {function} onChange - Callback when value changes
 * @param {number} min - Minimum value
 * @param {number} max - Maximum value
 * @param {number} step - Step increment
 * @param {string} unit - Unit to display (e.g., "kcal", "g/kg", "min")
 * @param {string} prefix - Prefix for value display (e.g., "+", "-")
 * @param {string} color - Accent color: "blue", "rose", "amber", "indigo" (default: "indigo")
 * @param {Array} markers - Optional array of marker labels [{value, label}] or strings for even distribution
 * @param {string} hint - Optional hint text below the slider
 */

const COLOR_CLASSES = {
  blue: "accent-blue-600",
  rose: "accent-rose-600",
  amber: "accent-amber-600",
  indigo: "accent-indigo-600",
};

const VALUE_COLOR_CLASSES = {
  blue: "text-blue-600",
  rose: "text-rose-600",
  amber: "text-amber-600",
  indigo: "text-indigo-600",
};

export default function RangeSlider({
  label,
  value,
  onChange,
  min,
  max,
  step = 1,
  unit = "",
  prefix = "",
  color = "indigo",
  markers = [],
  hint = null,
}) {
  const accentClass = COLOR_CLASSES[color] || COLOR_CLASSES.indigo;
  const valueColorClass = VALUE_COLOR_CLASSES[color] || VALUE_COLOR_CLASSES.indigo;

  return (
    <div className="space-y-2">
      <div className="flex justify-between items-center">
        <label className="text-sm font-medium text-gray-700">{label}</label>
        <span className={`${valueColorClass} font-semibold`}>
          {prefix}{value}{unit}
        </span>
      </div>
      
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className={`w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer ${accentClass}`}
      />
      
      {markers.length > 0 && (
        <div className="flex justify-between text-xs text-gray-400">
          {markers.map((marker, index) => (
            <span key={index}>
              {typeof marker === "string" ? marker : marker.label}
            </span>
          ))}
        </div>
      )}

      {hint && (
        <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded border border-amber-200">
          {hint}
        </p>
      )}
    </div>
  );
}